import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { day } from 'libs/day';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { ContentEvents } from '@app/src/app/minimal/services/ContentEvents';
import { EventCalendarService } from '@app/src/app/services/EventCalendar';

import { EventScheduleDataSource } from './EventSchedule.datasource';

interface IEventFeedFilter {
  groupHashes?: string;
  startTimestamp?: string;
}

@Component({
  templateUrl: './EventSchedule.component.html',
  styleUrls: ['./EventSchedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [EventScheduleDataSource],
})
export class EventScheduleComponent implements OnDestroy {
  feedFilter$: Observable<IEventFeedFilter>;

  startDate$: Observable<Date>;
  settingsSub: Subscription;

  private _destroyed = new ReplaySubject<void>(1);

  constructor(
    private eventCalendarService: EventCalendarService,
    private route: ActivatedRoute,
    private _cdr: ChangeDetectorRef,
    public ds: EventScheduleDataSource,
    private _contentEvents: ContentEvents,
  ) {
    this.route.params.subscribe(params => {
      if (params.quickFilter) {
        this.eventCalendarService.setQuickFilter(params.quickFilter);
      }
    });

    this.settingsSub = eventCalendarService.settings$.subscribe(settings => {
      this.ds.setSettings(settings);
      this._cdr.markForCheck();
    });

    this._contentEvents.onPostCreateSuccess.pipe(takeUntil(this._destroyed)).subscribe(ev => {
      if (ev.typeString === 'event') {
        this.ds.restartStream();
      }
    });

    this._contentEvents.onContentDeleted.pipe(takeUntil(this._destroyed)).subscribe(() => {
      this.ds.restartStream();
    });

    this.startDate$ = this.route.queryParams.pipe(
      map(params => params.start),
      distinctUntilChanged(),
      map((start: string) => {
        // replace dashes with slashes for safari support
        if (start) {
          start = start.replace(/-/g, '/');
        }
        return day(start).startOf('day').toDate();
      }),
    );

    this.eventCalendarService.setUseCalendarSettings(false);
  }

  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
    this.settingsSub.unsubscribe();
  }
}
